import * as React from 'react';
import { Link } from 'react-router-dom';

import { Stack } from '@mui/material';
import Box from '@mui/material/Box';

import dayjs from 'dayjs';
import { type MRT_ColumnDef } from 'material-react-table';

import { VennueAccessRequestItem, VenueAccessREnum } from '../../../graphql/API';
import { formatNumber } from '../../../utils/formatter';
import { getUserCategory } from '../../../utils/user';
import UserAvatarCategory from '../../Avatar/UserAvatarCategory/UserAvatarCategory';
import LoadingButton from '../../Button/LoadingButton';
import StatusLabel from '../../StatusLabel/StatusLabel';
import { OnAction } from './useAction';
import { buttonLoadingState, isOldRequest, mapToActionRequest } from './utils';
import TikTokTag from '../../TikTokTag/TikTokTag';
import InstagramTag from '../../InstagramTag/InstagramTag';

type UseColumnProps = {
  loadingState: Record<string, boolean>;
  onAction: OnAction;
  isBulkActionStarted?: boolean;
} & {
  onAccept?: (accessRequest: VennueAccessRequestItem | null) => void;
  onReject?: (accessRequest: VennueAccessRequestItem | null) => void;
  onRejectWithConfirmation?: (accessRequest: VennueAccessRequestItem | null) => void;
  onAcceptWithIncentive?: (accessRequest: VennueAccessRequestItem | null) => void;
  onAcceptOldRequest?: (accessRequest: VennueAccessRequestItem | null) => void;
};

export const useProductRequestsColumn: (
  props: UseColumnProps,
) => MRT_ColumnDef<VennueAccessRequestItem>[] = ({
  loadingState,
  onAction,
  onAccept,
  isBulkActionStarted = false,
  onReject,
  onRejectWithConfirmation,
  onAcceptWithIncentive,
  onAcceptOldRequest,
}) => [
      {
        accessorKey: 'user.picture.thumbnail',
        header: '',
        size: 80,
        enableSorting: false,
        Cell: ({ renderedCellValue, row }) =>
          renderedCellValue ? (
            <UserAvatarCategory
              src={String(renderedCellValue)}
              userCategory={getUserCategory(row.original.user?.tags ?? [])}
            />
          ) : null,
      },
      {
        accessorKey: 'userName',
        accessorFn: (row) =>
          row.user?.firstname || row.user?.lastname
            ? `${row.user?.firstname} ${row.user?.lastname}`
            : undefined,
        header: 'Name',
        size: 150,
        enableSorting: true,
        Cell: ({ renderedCellValue, row }) => (
          <Box
            component={Link}
            to={`/users/${row.original.userID}`}
            sx={{
              color: '#111',
              textDecoration: 'none',
              ':hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {renderedCellValue}
          </Box>
        ),
      },
      {
        accessorKey: 'followers',
        header: 'IG followers',
        size: 80,
        enableSorting: false,
        Cell: ({ row }) => formatNumber(row.original.user?.instagramFollowers || 0),
      },
      {
        accessorKey: 'instagramHandle',
        header: 'Instagram',
        size: 120,
        enableSorting: false,
        Cell: ({ row }) => row.original.user?.instagramHandle ? <InstagramTag username={row.original.user?.instagramHandle} /> : '-',
      },
      {
        accessorKey: 'tiktokHandle',
        header: 'TikTok',
        size: 120,
        enableSorting: false,
        Cell: ({ row }) => row.original.user?.tiktokHandle ? <TikTokTag tiktokHandle={row.original.user?.tiktokHandle} /> : '-',
      },
      {
        accessorKey: 'rating',
        header: 'Rating',
        size: 80,
        enableSorting: true,
        Cell: ({
          row: {
            original: { user, totalRatings },
          },
        }) => (
          <Box display={'flex'} alignItems={'center'} gap={'4px'}>
            <Box component='span' fontWeight='bold'>
              {user?.rating?.toFixed(1)}
            </Box>
            ({totalRatings})
          </Box>
        ),
      },
      {
        accessorKey: 'offerTitle',
        header: 'Product requested',
        size: 180,
        Cell: ({ row }) => (
          <Box
            component={Link}
            sx={{
              color: '#111',
              textDecoration: 'none',
              ':hover': {
                textDecoration: 'underline',
              },
            }}
            to={`/offers/${row.original.offerID}`}
          >
            {row.original.offer?.title}
          </Box>
        ),
      },
      {
        accessorKey: 'shippingAddress',
        header: 'Ship to',
        size: 200,
        enableSorting: false,
        Cell: ({ row }) =>
          row.original?.shippingAddress ? (
            <Box component={'span'}>
              {row.original?.shippingAddress?.city}, {row.original.shippingAddress?.country}
            </Box>
          ) : (
            '-'
          ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Requested',
        size: 130,
        enableSorting: true,
        Cell: ({ row }) =>
          row.original.createdAt ? (
            <Box display='flex' flexDirection='column'>
              <Box component='span' sx={{ color: '#111', fontSize: '0.875rem' }}>
                {dayjs(row.original.createdAt).format('MMM D, YYYY')}
              </Box>
              <Box component='span' sx={{ color: '#757575', fontSize: '0.75rem' }}>
                {dayjs(row.original.createdAt).fromNow()}
              </Box>
            </Box>
          ) : (
            '-'
          ),
      },
      {
        accessorKey: 'action',
        header: '',
        size: 80,
        enableSorting: false,
        Cell: ({ row }) => (
          <Stack spacing={2} direction='row'>
            <LoadingButton
              fullWidth
              variant='contained'
              onClick={() => {
                if (isOldRequest(row.original.createdAt) && onAcceptOldRequest) {
                  onAcceptOldRequest(row.original);
                  return;
                }
                if ((row.original.offer?.incentiveValue || 0) > 0) {
                  if (onAcceptWithIncentive) onAcceptWithIncentive(row.original);
                } else {
                  onAction(mapToActionRequest(row.original ?? undefined, VenueAccessREnum.accepted));
                  if (onAccept) onAccept(row.original);
                }
              }}
              {...buttonLoadingState(loadingState, row.original, VenueAccessREnum.accepted)}
              loading={isBulkActionStarted}
              disabled={isBulkActionStarted}
            >
              Accept
            </LoadingButton>
            <LoadingButton
              fullWidth
              variant='outlined'
              onClick={() => {
                if (onReject && (row.original.totalRequested || 0) >= 2) {
                  onReject(row.original);
                } else if (onRejectWithConfirmation) {
                  onRejectWithConfirmation(row.original);
                } else {
                  onAction(mapToActionRequest(row.original ?? undefined, VenueAccessREnum.declined));
                }
                // if (onAccept) onAccept(null);
              }}
              {...buttonLoadingState(loadingState, row.original, VenueAccessREnum.declined)}
              loading={isBulkActionStarted}
              disabled={isBulkActionStarted}
              sx={{ border: '1.5px solid #000' }}
            >
              Reject
            </LoadingButton>
          </Stack>
        ),
      },
    ];

export const useAccessRequestsColumn: (
  props: UseColumnProps,
) => MRT_ColumnDef<VennueAccessRequestItem>[] = ({ loadingState, onAction, onAccept }) => [
  {
    accessorKey: 'user.picture.thumbnail',
    header: '',
    size: 80,
    enableSorting: false,
    Cell: ({ renderedCellValue, row }) =>
      renderedCellValue ? (
        <UserAvatarCategory
          src={String(renderedCellValue)}
          userCategory={getUserCategory(row.original.user?.tags ?? [])}
        />
      ) : null,
  },
  {
    accessorKey: 'userName',
    accessorFn: (row) =>
      row.user?.firstname || row.user?.lastname
        ? `${row.user?.firstname} ${row.user?.lastname}`
        : undefined,
    header: 'Name',
    size: 150,
    enableSorting: true,
    Cell: ({ renderedCellValue, row }) => (
      <Box
        component={Link}
        to={`/users/${row.original.userID}`}
        sx={{
          textDecoration: 'none',
          ':hover': {
            textDecoration: 'underline',
          },
          color: '#1E55AA',
        }}
      >
        {renderedCellValue}
      </Box>
    ),
  },
  {
    accessorKey: 'offerTitle',
    header: 'Request access for',
    size: 200,
    Cell: ({ row }) => (
      <Box
        component={Link}
        sx={{
          color: '#111',
          textDecoration: 'none',
          ':hover': {
            textDecoration: 'underline',
          },
        }}
        to={`/offers/${row.original.offerID}`}
      >
        {row.original.offer?.title}
      </Box>
    ),
  },
  {
    accessorKey: 'lifestyle',
    header: 'Member Category',
    size: 150,
    Cell: ({
      row: {
        original: { user },
      },
    }) =>
      user?.lifestyle !== undefined ? (
        <StatusLabel
          type='userCategory'
          status={user?.lifestyle ? 'BP LIFESTYLE' : 'BP SELECT'}
          emptyBackground
        />
      ) : (
        '-'
      ),
  },
  {
    accessorKey: 'action',
    header: '',
    size: 80,
    enableSorting: false,
    Cell: ({ row }) => (
      <Stack spacing={2} direction='row'>
        <LoadingButton
          fullWidth
          variant='outlined'
          onClick={() => {
            onAction(mapToActionRequest(row.original ?? undefined, VenueAccessREnum.declined));
            if (onAccept) onAccept(null);
          }}
          {...buttonLoadingState(loadingState, row.original, VenueAccessREnum.declined)}
        >
          Reject
        </LoadingButton>
        <LoadingButton
          fullWidth
          variant='contained'
          onClick={() => {
            onAction(mapToActionRequest(row.original ?? undefined, VenueAccessREnum.accepted));
            if (onAccept) onAccept(row.original);
          }}
          {...buttonLoadingState(loadingState, row.original, VenueAccessREnum.accepted)}
        >
          Accept
        </LoadingButton>
      </Stack>
    ),
  },
];
